import { HttpParams } from '@angular/common/http';
import { Component, forwardRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import * as _ from 'lodash';
import * as moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { THING_DEFINITIONS } from '../../../common/endpoints';
import { CustomerService } from '../../../dashboard-area/customer/customer.service';
import { AlertService } from '../../../dashboard-area/shared/alert.service';
import { AlertDefinition, CustomPropertyDefinition, LocationAlertDefinition, Thing, ThingDefinition } from '../../../model';
import { AlertDefinitionService } from '../../../service/alert-definition.service';
import { AuthenticationService } from '../../../service/authentication.service';
import { ContextService } from '../../../service/context.service';
import { CustomPropertyService, CustomPropertyType } from '../../../service/custom-property.service';
import { CustomDateRange, DateRangeService } from '../../../service/date-range.service';
import { FieldService } from '../../../service/field.service';
import { HttpService } from '../../../service/http.service';
import { AbstractContextService } from '../../../shared/class/abstract-context-service.class';
import { AbstractThingContextService } from '../../../shared/class/abstract-thing-context-service.class';
import { FormOption } from '../../../shared/form-editor/form-field-type/form-option.interface';
import { LocalizationPipe } from '../../../shared/pipe';
import { ManualEventReportDialogComponent } from '../../../widget/manual-event-report-dialog/manual-event-report-dialog.component';
import { AbstractAdvancedSearchComponent } from '../abstract-advanced-search.component';
import { EventAdvancedSearchAddPropertiesDialog } from './event-advanced-search-add-properties-dialog.component';

@Component({
    selector: 'event-advanced-search',
    template: require('./event-advanced-search.component.html'),
    styles: [require('../thing-advanced-search/thing-advanced-search.component.css')],
    providers: [CustomerService]
})
export class EventAdvancedSearchComponent extends AbstractAdvancedSearchComponent implements OnInit {

    @Input() isHistorical: boolean;

    @Input() periodDisabled: boolean;

    @ViewChild(EventAdvancedSearchAddPropertiesDialog) addPropertiesDialog: EventAdvancedSearchAddPropertiesDialog;

    private addCustomerSearchField: boolean;
    private searchDataInitialized: boolean;
    private alertDefinitionsTree: any[];
    private thingDefinitionTypes: { value: string, label: string }[];
    private eventSeverities: { value: string, label: string }[];
    private locationAlertDefinitions: { value: string, label: string }[];
    private alertDefinitionGroups: { groupId: string, groupLabel: string, groupValues: FormOption[] }[] = [];
    private defaultCustomDateRange: CustomDateRange;
    private eventTopics: { value: string, label: string }[];
    private manualAlertDefinitions: AlertDefinition[];
    private currentThing: Thing;

    defaultProperties: { name: string, label: string }[] = [];
    thingDefinitionProperties: CustomPropertyDefinition[];

    constructor(
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => LocalizationPipe)) localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => FieldService)) fieldService: FieldService,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AbstractContextService)) private contextService: AbstractContextService,
        @Inject(forwardRef(() => CustomerService)) private customerService: CustomerService,
        @Inject(forwardRef(() => AlertDefinitionService)) private alertDefinitionService: AlertDefinitionService,
        @Inject(forwardRef(() => AlertService)) private alertService: AlertService,
        @Inject(forwardRef(() => DateRangeService)) private dateRangeService: DateRangeService,
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => CustomPropertyService)) private customPropertyService: CustomPropertyService,
        @Inject(forwardRef(() => MatDialog)) private dialog: MatDialog
    ) { super(localizationPipe, fieldService) }

    ngOnInit(): void {
        this.localStorageKey = this.queryId || (this.isHistorical ? 'historicalEventAdvancedSearchFieldsValues' : 'eventAdvancedSearchFieldsValues');
        this.savedFieldsValues = localStorage.getItem(this.localStorageKey) ? JSON.parse(localStorage.getItem(this.localStorageKey)) : null;
        const customerId = this.authenticationService.getUser().customerId || (this.contextService.getCurrentCustomer() ? this.contextService.getCurrentCustomer().id : null)
            || (this.authenticationService.isLocationUser() ? (ContextService.getCustomerFromLocation(this.authenticationService.getUser().location) ? ContextService.getCustomerFromLocation(this.authenticationService.getUser().location).id : null) : null);
        this.addCustomerSearchField = ((this.authenticationService.isOrganizationUser() || this.authenticationService.isPartnerUser()) && !customerId) || this.authenticationService.getUser().hostCustomers?.length > 0;
        const userFields = _.cloneDeep(this.authenticationService.getUser().alertsSearchFields);
        this.searchFields = this.getSearchFields(userFields);
        this.thingDefinitionProperties = this.customPropertyService.getCustomPropertyDefinitionByType(CustomPropertyType.ThingDefinition).filter(def => def.searchable);
        this.getAlertProperties();
        if (this.isHistorical) {
            this.defaultCustomDateRange = this.dateRangeService.getCustomDateRangeByName("LAST_7_DAYS");
        }
        this.currentThing = this.thingContextService.getCurrentThing();
        this.initSimpleSearchActions();
        this.getSearchData().then(() => {
            if (this.query && this.query.length) {
                this.getEncodedQueryFields();
            }
            if (this.queryFieldRef) {
                this.subscribeToQueryFieldRef();
            } else {
                if (this.savedFieldsValues || this.alwaysExpanded || (this.isHistorical && this.thingContextService.getCurrentWorkSession())) {
                    this.showHideAdvancedSearch().then(() => this.waitForAdvancedSearchRenderedAndPerformSearch());
                } else if (this.encodedQueryFields) {
                    this.loadData(null, this.encodedQueryFields);
                }
            }
        });
    }

    advancedSearch($event?): void {
        this.simpleSearchKey = null;
        const rawValue = this.advancedSearchEditor.getObjectValue();
        const key = this.advancedSearchBarEditor.getObjectValue()['key'];
        const rangeValue: { range: DateRange<moment.Moment>, rangeName: string } = rawValue['period'];
        const range: DateRange<moment.Moment> = rangeValue?.rangeName ? this.dateRangeService.getCustomDateRangeByName(rangeValue.rangeName)?.range : rangeValue?.range;
        const allAlertDefinitions = _.get(rawValue, "alertDefinitionIds") ? _.get(rawValue, "alertDefinitionIds") : [];
        const alertDefinitionIds = allAlertDefinitions.filter((id: string) => !id.startsWith("LOCATION_ALERT_DEF:"));
        let locationAlertDefinitionIds = allAlertDefinitions.filter((id: string) => id.startsWith("LOCATION_ALERT_DEF:"));
        locationAlertDefinitionIds = locationAlertDefinitionIds.map(id => { return id.substring(19) });
        const fields = {
            key: key,
            thingDefinitions: alertDefinitionIds.length || locationAlertDefinitionIds.length ? null : (rawValue['thingDefinitionIds']?.length ? rawValue['thingDefinitionIds'] : null),
            customer: rawValue['customer'] || null,
            eventSeverity: rawValue['eventSeverity'] || null,
            startTimestamp: range?.start?.valueOf(),
            endTimestamp: range?.end?.valueOf(),
            alertDefinitions: alertDefinitionIds?.length ? alertDefinitionIds : null,
            locationAlertDefinitions: locationAlertDefinitionIds?.length ? locationAlertDefinitionIds : null,
            topics: rawValue['topics'] || null
        };
        let fieldsToSave = {
            key: key,
            thingDefinitions: alertDefinitionIds.length || locationAlertDefinitionIds.length ? null : rawValue['thingDefinitionIds'],
            customer: rawValue['customer'],
            eventSeverity: rawValue['eventSeverity'],
            alertDefinitions: allAlertDefinitions,
            topics: rawValue['topics'] || null
        };
        if (rangeValue?.rangeName) {
            fieldsToSave['rangeName'] = rangeValue?.rangeName;
        } else {
            fieldsToSave['startTimestamp'] = range?.start?.valueOf();
            fieldsToSave['endTimestamp'] = range?.end?.valueOf();
        }
        let encodedBody = Object.assign({}, fields, this.propertiesInputs ? this.propertiesInputs.getEncodedBody() : null);
        let fieldsToSaveBody = Object.assign({}, fieldsToSave, this.propertiesInputs ? this.propertiesInputs.getBody() : null);
        if (this.query && this.query.length) {
            encodedBody = Object.assign({}, encodedBody, this.encodedQueryFields);
            fieldsToSaveBody = this.removeQueryFields(fieldsToSaveBody);
        }
        if (this.isHistorical && this.thingContextService.getCurrentWorkSession()) {
            delete fieldsToSaveBody['startTimestamp'];
            delete fieldsToSaveBody['endTimestamp'];
            delete fieldsToSaveBody['rangeName'];
        }
        this.handleSearchFieldSelectionInputs(encodedBody, fieldsToSaveBody);
        this.updateLocalStorage(fieldsToSaveBody);
        const encodedBodyValues = Object.keys(encodedBody).map(el => encodedBody[el]);
        if (encodedBodyValues.some(el => el != null)) {
            this.loadData(key, encodedBody);
        } else {
            this.loadData();
        }
        if ($event) {
            const eventObject = $event.currentTarget;
            eventObject.blur();
        }
    }

    protected initConfigurations(): Promise<void> {
        let advancedSearchBarConfiguration = [];
        advancedSearchBarConfiguration.push({ name: 'key', type: 'SEARCH', value: this.getValue('key') || this.simpleSearchKey });
        this.advancedSearchBarConfiguration = advancedSearchBarConfiguration;
        return this.getSearchData().then(() => {
            let advancedSearchConfiguration = [];
            if (this.addCustomerSearchField) {
                advancedSearchConfiguration.push({ name: 'customer', label: 'customerProperty', type: 'CUSTOMER_SEARCH', value: this.getValue('customer'), disabled: this.isQueryField('customer'), defaultValue: this.isQueryField('customer') ? this.getValue('customer') : null });
            }
            advancedSearchConfiguration.push({ name: 'eventSeverity', label: 'alertDefinitionSeverityProperty', type: 'STRING', selectionMode: 'MAT_SELECTION', values: this.eventSeverities, value: this.getValue('eventSeverity'), multipleSelection: true, disabled: this.isQueryField('eventSeverity'), defaultValue: this.isQueryField('eventSeverity') ? this.getValue('eventSeverity') : null, placeholder: "All Severities" });
            if (!this.periodDisabled) {
                advancedSearchConfiguration.push({ name: 'period', 'label': 'periodProperty', type: 'PERIOD', value: this.getDateRange() || { range: null, rangeName: this.defaultCustomDateRange?.name }, defaultValue: this.isQueryField('startTimestamp') && this.isQueryField('endTimestamp') ? this.getDateRange() : null });
            }
            if (this.thingDefinitionTypes.length > 1 && !this.currentThing) {
                advancedSearchConfiguration.push({ name: 'thingDefinitionIds', label: 'thingDefinitionsTabItem', type: 'STRING', selectionMode: 'MAT_SELECTION', values: this.thingDefinitionTypes, value: this.getValue('thingDefinitions'), multipleSelection: true, disabled: this.isQueryField('thingDefinitions'), defaultValue: this.isQueryField('thingDefinitions') ? this.getValue('thingDefinitions') : null, placeholder: "All Thing Definitions" });
            }
            if (this.alertDefinitionsTree && this.alertDefinitionsTree.length) {
                this.alertDefinitionGroups = this.alertDefinitionsTree.map(el => { return { groupId: el.id, groupLabel: el.label, groupValues: el.children.map(child => { return { value: child.id, label: child.label } }) } });
            }
            if (this.locationAlertDefinitions && this.locationAlertDefinitions.length) {
                this.alertDefinitionGroups.push({ groupId: null, groupLabel: this.localizationPipe.transform('Location Alert Definitions'), groupValues: this.locationAlertDefinitions });
            }
            if (this.alertDefinitionGroups && this.alertDefinitionGroups.length) {
                advancedSearchConfiguration.push({ name: 'alertDefinitionIds', label: 'alertDefinitionTypeProperty', type: 'STRING', selectionMode: 'MAT_SELECTION', values: [], value: this.getValue('alertDefinitions'), multipleSelection: true, enableMatSelectGroups: true, matSelectGroupValues: this.alertDefinitionGroups, disabled: this.isQueryField('alertDefinitions'), defaultValue: this.isQueryField('alertDefinitions') ? this.getValue('alertDefinitions') : null, placeholder: "All Events" });
            }
            advancedSearchConfiguration.push({ name: 'topics', label: 'topicProperty', type: 'STRING', selectionMode: 'MAT_SELECTION', values: this.eventTopics, value: this.getValue('topics'), multipleSelection: true, disabled: this.isQueryField('topics'), defaultValue: this.isQueryField('topics') ? this.getValue('topics') : null, placeholder: "All Topics" });
            this.fieldsPerRow = 3;
            this.advancedSearchConfiguration = advancedSearchConfiguration;
            setTimeout(() => this.updateAlertDefinitionGroup('thingDefinitionIds'), 10);
        });
    }

    private getSearchData(): Promise<void> {
        if (!this.searchDataInitialized) {
            this.searchDataInitialized = true;
            let promises = [];
            promises.push(firstValueFrom(this.httpService.get<ThingDefinition[]>(THING_DEFINITIONS)).catch(() => []));
            if (this.currentThing) {
                promises.push(this.alertDefinitionService.getAlertDefinitionsByThingDefinitionId(this.currentThing.thingDefinitionId).catch(() => []));
            } else {
                promises.push(this.alertService.getAlertDefinitions(false, true).catch(() => []));
            }
            promises.push(this.alertDefinitionService.getLocationAlertDefintions().catch(() => []));
            promises.push(this.alertDefinitionService.getEventTopics().catch(() => []));
            if (this.addCustomerSearchField) {
                if (this.authenticationService.getUser().hostCustomers?.length) {
                    const customerIds = this.authenticationService.getUser().hostCustomers.map(hc => { return hc.customerId });
                    promises.push(Promise.all(customerIds.map(id => { return this.customerService.getCustomerById(id) })).catch(() => []));
                } else {
                    let partnerParam = null;
                    if (this.contextService.getCurrentPartner()) {
                        partnerParam = new HttpParams().set('partnerId', this.contextService.getCurrentPartner().id);
                    }
                }
            }
            return Promise.all(promises).then(results => {
                this.eventSeverities = [
                    { value: '1', label: 'Operation' },
                    { value: '2', label: 'Minor Anomaly' },
                    { value: '3', label: 'Major Anomaly' },
                    { value: '4', label: 'Failure' }
                ];
                this.eventTopics = results[3].map(t => { return { value: t.name, label: t.label } });
                this.thingDefinitionTypes = results[0].map((td: ThingDefinition) => { return { value: td.id, label: td.name } });
                this.alertDefinitionsTree = this.alertService.buildTree(results[1]);
                this.locationAlertDefinitions = results[2].map((locDef: LocationAlertDefinition) => { return { value: "LOCATION_ALERT_DEF:" + locDef.id, label: locDef.name }; });
                this.handleManualAlertDefinitions(results[1]);
            });
        }
        return Promise.resolve();
    }

    getEncodedQueryFields(): void {
        let fields = [];
        this.query.forEach(el => {
            if (el.property == 'severity') {
                el.property = 'severities';
            }
            if (el.property == 'topic') {
                el.property = 'topics';
            }
            if (el.property == 'group' || el.property == 'duration' || el.property == 'name' || this.advancedSearchAddibleProperties.some(p => p.name == el.property || "properties." + p.name == el.property)) {
                let propDef: CustomPropertyDefinition = null;
                if (el.property.startsWith('thingDefinition.properties.')) {
                    propDef = this.thingDefinitionProperties.find(prop => el.property == "thingDefinition.properties." + prop.name);
                }
                fields[el.property] = this.getQueryValueWithSuffixes(el.value, el.predicate, propDef);
            } else {
                fields[el.property] = el.value;
            }
        });
        this.encodedQueryFields = fields;
    }

    private getDateRange(): { range: DateRange<moment.Moment>, rangeName: string } {
        if (this.isHistorical && this.thingContextService.getCurrentWorkSession()) {
            const ws = this.thingContextService.getCurrentWorkSession()
            const start = ws.startTimestamp;
            const end = ws.endTimestamp || new Date().getTime();
            return { range: new DateRange(moment(start), moment(end)), rangeName: null };
        } else {
            const rangeName = this.getValue('rangeName');
            if (rangeName) {
                return { range: null, rangeName: rangeName };
            } else {
                const start = this.getValue('startTimestamp');
                const end = this.getValue('endTimestamp');
                if (start && end) {
                    return { range: new DateRange(moment(start), moment(end)), rangeName: null };
                }
            }
            return null;
        }
    }

    updateAlertDefinitionGroup(name: string): void {
        if (name == 'thingDefinitionIds' && this.advancedSearchEditor) {
            const rawValues = this.advancedSearchEditor.getObjectValue();
            const thingDefintionIds: string[] = _.get(rawValues, 'thingDefinitionIds') || [];
            const configurations = _.cloneDeep(this.advancedSearchConfiguration);
            const groupValues = this.alertDefinitionGroups.filter(group => thingDefintionIds.includes(group.groupId));
            if (configurations.find(el => el.name == 'alertDefinitionIds')) {
                if (thingDefintionIds && thingDefintionIds.length) {
                    if (groupValues && groupValues.length) {
                        configurations.find(el => el.name == 'alertDefinitionIds').matSelectGroupValues = groupValues;
                    } else {
                        configurations.splice(configurations.indexOf(el => el.name == 'alertDefinitionIds'), 1);
                    }
                } else {
                    configurations.find(el => el.name == 'alertDefinitionIds').matSelectGroupValues = this.alertDefinitionGroups;
                }
            } else {
                if (thingDefintionIds && thingDefintionIds.length) {
                    if (groupValues && groupValues.length) {
                        configurations.push({ name: 'alertDefinitionIds', label: 'alertDefinitionTypeProperty', type: 'STRING', selectionMode: 'MAT_SELECTION', values: [], multipleSelection: true, enableMatSelectGroups: true, matSelectGroupValues: groupValues });
                    }
                } else if (this.alertDefinitionGroups && this.alertDefinitionGroups.length) {
                    configurations.push({ name: 'alertDefinitionIds', label: 'alertDefinitionTypeProperty', type: 'STRING', selectionMode: 'MAT_SELECTION', values: [], multipleSelection: true, enableMatSelectGroups: true, matSelectGroupValues: this.alertDefinitionGroups });
                }
            }
            configurations.forEach(conf => {
                if (conf.name != 'alertDefinitionIds') {
                    conf.value = rawValues[conf.name];
                }
            });
            this.advancedSearchConfiguration = configurations;
        }
    }

    openAddMorePropertiesDialog(): void {
        if (this.useExternalAddPropertiesDialog) {
            this.emitOpenExternalAddPropertiesDialogAction(this.advancedSearchAddibleProperties);
        } else {
            this.addPropertiesDialog.open();
        }
    }

    private getAlertProperties(): void {
        this.advancedSearchAddibleProperties = [];
        if (this.thingDefinitionProperties) {
            this.updateAdvancedSearchAddibleProperties(this.thingDefinitionProperties, 'thingDefinition.properties.', null);
        }
    }

    private handleManualAlertDefinitions(alertDefinitions: AlertDefinition[]): void {
        if (this.currentThing) {
            this.manualAlertDefinitions = alertDefinitions?.filter(ad => ad.manualActivation && (!ad.serviceLevelIds?.length || ad.serviceLevelIds.includes(this.currentThing.serviceLevel?.id)));
            this.simpleSearchActions.push(
                {
                    name: 'REPORT_EVENT',
                    title: 'reportEventProperty',
                    visible: this.manualAlertDefinitions.length > 0
                }
            );
        }
    }

    performSimpleSearchAction(action: string): void {
        switch (action) {
            case "REPORT_EVENT":
                this.openManualReportDialog();
                break;
            case "EDIT_TABLE_COLUMNS_EVENT":
                this.emitEditTableColumnsAction();
                break;
            default:
                break;
        }
    }

    private openManualReportDialog(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.minWidth = '25%';
        dialogConfig.maxWidth = '428px';
        dialogConfig.data = {
            thing: this.currentThing,
            alertDefinitions: this.manualAlertDefinitions
        };
        this.dialog.open(ManualEventReportDialogComponent, dialogConfig);
    }

    private initSimpleSearchActions(): void {
        this.simpleSearchActions = [
            {
                name: 'EDIT_TABLE_COLUMNS_EVENT',
                title: 'editColumnsProperty',
                visible: this.editTableColumnsEnabled
            }
        ];
    }

}